import React, { useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, CssBaseline } from '@mui/material';
import 'react-toastify/dist/ReactToastify.css';

import StudentDashboard from './pages/student/StudentDashboard';
import Profile from './pages/student/Profile';
import PrivateRoute from './auth/student/PrivateRoute';
import PublicRoute from './auth/student/PublicRoute';
import Register from './auth/student/Register';
import Login from './auth/student/Login';
import MockTestPage from './pages/student/MockTestPage';
import QuizPage from './pages/student/QuizPage';
import ForgotPasswordPage from './auth/student/ForgotPasswordPage';
import VerifyOtpPage from './auth/student/VerifyOtpPage';
import ResetPasswordPage from './auth/student/ResetPasswordPage';
import RootRedirect from './auth/student/RootRedirect';
import Chat from './pages/student/Chat';
import Vad from './pages/student/vad';
import NotFoundPage from './pages/NotFoundPage';

import './App.css';
import ResultsPage from './pages/student/ResultsPage';
import DetailedResultsPage from './pages/student/DetailedResultsPage';
import MockScoreBoard from './pages/student/MockScoreBoard';
import MockTestList from './pages/student/MockTestList';
import Pricing from './pages/student/Pricing';
import SubscriptionSuccessPage from './pages/student/SubscriptionSuccessPage';
import SubscriptionCancelPage from './pages/student/SubscriptionCancelPage';
import SubscriptionManagementPage from './pages/student/SubscriptionManagementPage';
import ExamMode from './pages/student/ExamMode';
import MyExamTopics from './pages/student/MyExamTopics';
import StudyPlanPage from './pages/student/StudyPlanPage';
import ZapTalkMoodTrackerPage from './pages/student/ZapTalkMoodTrackerPage';
import PartnersPage from './pages/ep/PartnersPage';
import EPPrivateRoute from './auth/ep/EPPrivateRoute';
import EPDashboard from './pages/ep/EPDashboard';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminPrivateRoute from './auth/admin/AdminPrivateRoute';
import ParentDashboard from './pages/parent/ParentDashboard';
import ParentPrivateRoute from './auth/parent/ParentPrivateRoute';
import ParentRegister from './auth/parent/ParentRegister';
import ParentStudentDetail from './pages/parent/ParentStudentDetail';
import ParentMockTestList from './pages/parent/ParentMockTestList';
import ParentScoreboardPage from './pages/parent/ParentScoreboardPage';
import ParentStudentNotifications from './pages/parent/ParentStudentNotifications';
import FeaturesPage from './pages/public/FeaturesPage';
import AboutPage from './pages/public/AboutPage';
import MatiyariPage from './pages/public/MatiyariPage';
import { getMuiTheme } from './theme';
import GlobalStyles from './theme/GlobalStyles';
import { LanguageProvider } from './context/LanguageContext';
import { ColorModeProvider, useColorMode } from './context/ColorModeContext';
import SessionWatcher from './components/auth/SessionWatcher';
import Seo from './components/seo/Seo';

const queryClient = new QueryClient();

interface PublicSeoConfig {
  title: string;
  description: string;
  canonicalPath: string;
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
}

const PUBLIC_SEO_CONFIG: Record<string, PublicSeoConfig> = {
  '/': {
    title: 'ZapTick AI | AI Tutor for Smarter Exam Preparation',
    description:
      'ZapTick AI helps students improve exam results with adaptive AI tutoring, mock tests, instant feedback, and progress tracking for families.',
    canonicalPath: '/',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'ZapTick AI',
        url: 'https://zaptick.ai/',
        logo: 'https://zaptick.ai/logo512.png',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'ZapTick AI',
        url: 'https://zaptick.ai/',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'ZapTick AI',
        applicationCategory: 'EducationalApplication',
        operatingSystem: 'Web',
        url: 'https://zaptick.ai/',
      },
    ],
  },
  '/features': {
    title: 'Features | ZapTick AI',
    description:
      'Explore ZapTick AI features: adaptive tutoring, exam-focused practice, instant evaluation, parent tracking, and progress analytics.',
    canonicalPath: '/features',
  },
  '/about': {
    title: 'About ZapTick AI',
    description:
      'Learn about ZapTick AI, our mission to improve student outcomes, and how we build practical AI tools for students, parents, and teachers.',
    canonicalPath: '/about',
  },
  '/partners': {
    title: 'Enrollment Partners | ZapTick AI',
    description:
      'Join the ZapTick AI partner program to help students succeed, track performance, and earn commission through verified enrollments.',
    canonicalPath: '/partners',
  },
  '/matiyari': {
    title: 'MATIYARI Free Education Registration | ZapTick AI',
    description:
      'Register your interest for the MATIYARI special free education subscription flow on ZapTick AI.',
    canonicalPath: '/matiyari',
  },
};

const PRIVATE_OR_AUTH_ROUTE_PREFIXES = [
  '/login',
  '/register',
  '/parent-register',
  '/forgot-password',
  '/verify-otp',
  '/reset-password',
  '/dashboard',
  '/profile',
  '/mock',
  '/quiz',
  '/results',
  '/detailed-results',
  '/score',
  '/pricing',
  '/subscription',
  '/subscription-management',
  '/mock-test',
  '/chat',
  '/exam',
  '/zaptalk-mood-tracker',
  '/vad',
  '/exam-mode',
  '/your-exams',
  '/study-plan',
  '/ep-dashboard',
  '/admin-dashboard',
  '/parent-dashboard',
  '/parent',
];

const hasRoutePrefix = (pathname: string, prefix: string) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

const RouteSeoManager: React.FC = () => {
  const { pathname } = useLocation();
  const publicSeo = PUBLIC_SEO_CONFIG[pathname];

  if (publicSeo) {
    return (
      <Seo
        title={publicSeo.title}
        description={publicSeo.description}
        canonicalPath={publicSeo.canonicalPath}
        jsonLd={publicSeo.jsonLd}
        robots="index,follow"
      />
    );
  }

  const isPrivateOrAuthRoute = PRIVATE_OR_AUTH_ROUTE_PREFIXES.some((prefix) =>
    hasRoutePrefix(pathname, prefix)
  );

  if (isPrivateOrAuthRoute) {
    return (
      <Seo
        title="ZapTick AI Platform"
        description="Secure learning tools and dashboards for students, parents, and education teams."
        robots="noindex,nofollow"
      />
    );
  }

  return (
    <Seo
      title="Page Not Found | ZapTick AI"
      description="The requested page does not exist on ZapTick AI."
      robots="noindex,nofollow"
    />
  );
};

const AppShell: React.FC = () => {
  const { mode } = useColorMode();
  const theme = useMemo(() => getMuiTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles />
      <Router>
        <RouteSeoManager />
        <LanguageProvider>
          <div className="App">
            <SessionWatcher />
            <Routes>
              {/* Root route based on auth status */}
              <Route path="/" element={<RootRedirect />} />

              {/* Public marketing pages */}
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/matiyari" element={<MatiyariPage />} />

              {/* Public routes */}
              <Route element={<PublicRoute />}>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/parent-register" element={<ParentRegister />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/verify-otp" element={<VerifyOtpPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
              </Route>

              {/* Partners Page - Public route */}
              <Route path="/partners" element={<PartnersPage />} />

              {/* Protected EP routes */}
              <Route element={<EPPrivateRoute />}>
                <Route path="/ep-dashboard" element={<EPDashboard />} />
              </Route>

              {/* Protected Admin routes */}
              <Route element={<AdminPrivateRoute />}>
                <Route path="/admin-dashboard" element={<AdminDashboard />} />
              </Route>

              {/* Protected Parent routes */}
              <Route element={<ParentPrivateRoute />}>
                <Route path="/parent-dashboard" element={<ParentDashboard />} />
                <Route path="/parent/student/:childId" element={<ParentStudentDetail />} />
                <Route path="/parent/student/:childId/scores" element={<ParentScoreboardPage />} />
                <Route path="/parent/student/:childId/mock-test/:subject" element={<ParentMockTestList />} />
                <Route path="/parent/student/:childId/notifications" element={<ParentStudentNotifications />} />
              </Route>

              {/* Protected routes */}
              <Route element={<PrivateRoute />}>
                <Route path="/dashboard" element={<StudentDashboard />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/mock" element={<MockTestPage />} />
                <Route path="/quiz" element={<QuizPage />} />
                <Route path="/results" element={<ResultsPage />} />
                <Route path="/detailed-results" element={<DetailedResultsPage />} />
                <Route path="/score" element={<MockScoreBoard />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
                <Route path="/subscription/cancel" element={<SubscriptionCancelPage />} />
                <Route path="/subscription-management" element={<SubscriptionManagementPage />} />
                <Route path="/mock-test/:subject" element={<MockTestList />} />
                <Route path="/chat" element={<Chat />} />
                <Route path="/exam/:examId" element={<Chat />} />
                <Route path="/zaptalk-mood-tracker" element={<ZapTalkMoodTrackerPage />} />
                <Route path="/vad" element={<Vad />} />
                <Route path="/exam-mode" element={<ExamMode />} />
                <Route path="/your-exams" element={<MyExamTopics />} />
                <Route path="/study-plan/:topicId" element={<StudyPlanPage />} />
              </Route>

              {/* 404 Route */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            <ToastContainer position="top-right" autoClose={2000} theme={mode} />
          </div>
        </LanguageProvider>
      </Router>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ColorModeProvider>
        <AppShell />
      </ColorModeProvider>
    </QueryClientProvider>
  );
};

export default App;
